import React from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  FormControlLabel, 
  Checkbox, 
  Collapse, 
  IconButton, 
  Tooltip,
  Alert
} from "@mui/material";

// MUIアイコン
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';

/**
 * 供給詳細入力コンポーネント - 機能フラグ対応版
 */
const SupplyDetails = ({
  supplyAmount,
  supplyAmountError,
  supplyDate,
  isExceptional,
  isTankFull,
  manualRemainingAmount,
  manualRemainingError,
  selectedClientProduct,
  skipRemainingUpdate = false,
  onSkipRemainingUpdateChange,
  onSupplyAmountChange,
  onSupplyDateChange,
  onExceptionalChange,
  onTankFullChange,
  onManualRemainingAmountChange
}) => {
  // 製品が選択されているかどうか
  const isProductSelected = selectedClientProduct !== null;
  
  // 🚀 機能フラグによる表示制御
  const predictionEnabled = selectedClientProduct?.predictionEnabled === true;
  const tankInstalled = selectedClientProduct?.tankInstalled === true;
  const hasTankCapacity = selectedClientProduct?.totalCapacity != null;
  
  // 残量関連UIを表示するかどうか
  const shouldShowRemainingSection = isProductSelected && predictionEnabled && !skipRemainingUpdate;
  
  // 統一された入力フィールドスタイル
  const unifiedFieldStyle = {
    mb: 3,
    '& .MuiInputBase-root': {
      minHeight: { xs: 56, sm: 48 },
    },
    '& .MuiInputLabel-root': {
      fontSize: { xs: '1rem', sm: '0.95rem' }
    }
  };
  
  return (
    <>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 2, 
          fontWeight: 500,
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        供給詳細
      </Typography>
      
      {/* 供給量 */}
      <TextField
        label="供給量 (L)"
        fullWidth
        value={supplyAmount}
        onChange={(e) => {
          const value = e.target.value;
          if (value === "" || /^\d+$/.test(value)) {
            onSupplyAmountChange(value);
          }
        }}
        error={Boolean(supplyAmountError)}
        helperText={supplyAmountError}
        sx={unifiedFieldStyle}
        inputProps={{ 
          inputMode: "numeric", 
          pattern: "[0-9]*",
          min: "1",
          max: "200000"
        }}
      />

      {/* 供給日 */}
      <TextField
        label="供給日"
        type="date"
        fullWidth
        value={supplyDate}
        onChange={(e) => onSupplyDateChange(e.target.value)}
        InputProps={{
          inputProps: {
            max: new Date().toISOString().split("T")[0]
          },
          startAdornment: (
            <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
          )
        }}
        InputLabelProps={{ shrink: true }}
        sx={{
          ...unifiedFieldStyle,
          '& input[type="date"]': {
            fontSize: { xs: '1rem', sm: '0.95rem' },
            padding: { xs: '16px 14px', sm: '12px 14px' }
          }
        }}
      />

      {/* 🚀 機能フラグによる残量更新スキップオプション表示制御 */}
      {isProductSelected && predictionEnabled && (
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={skipRemainingUpdate}
                onChange={(e) => onSkipRemainingUpdateChange?.(e.target.checked)}
                color="primary"
              />
            }
            label="残量情報を更新しない（残量予測が不要な場合）"
          />
          
          {/* スキップ時の注意メッセージ */}
          {skipRemainingUpdate && (
            <Alert 
              severity="info" 
              icon={<InfoOutlinedIcon />}
              sx={{ 
                mt: 2,
                '& .MuiAlert-message': { fontSize: '0.875rem' },
                py: 1
              }}
            >
              残量情報は更新されません。必要に応じて「残量更新」タブから手動で更新してください。
            </Alert>
          )}
        </Box>
      )}

      {/* 🚀 機能フラグによる残量セクション表示制御 */}
      {shouldShowRemainingSection && (
        <Box sx={{ mb: 3 }}>
          {tankInstalled && hasTankCapacity ? (
            // パターン1: タンク設置済み + 容量設定済み → 満タンチェック
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isTankFull}
                    onChange={(e) => onTankFullChange(e.target.checked)}
                    color="primary"
                    sx={{
                      padding: { xs: 1.5, sm: 1 }
                    }}
                  />
                }
                label={`供給後、タンクは満タン（${selectedClientProduct.totalCapacity}L）になりましたか？`}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.95rem', sm: '0.875rem' }
                  }
                }}
              />
              
              {/* 満タンでない場合、残量入力フィールドを表示 */}
              <Collapse in={!isTankFull}>
                <Box sx={{ mt: 2, ml: 4 }}>
                  <TextField
                    label="供給後の残量 (L)"
                    fullWidth
                    value={manualRemainingAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d+$/.test(value)) {
                        onManualRemainingAmountChange(value);
                      }
                    }}
                    error={Boolean(manualRemainingError)}
                    helperText={manualRemainingError || "供給後の正確な残量を入力してください"}
                    InputProps={{
                      startAdornment: (
                        <WaterDropIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      ),
                      inputProps: {
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        min: "0",
                        max: selectedClientProduct.totalCapacity
                      }
                    }}
                  />
                </Box>
              </Collapse>
            </>
          ) : tankInstalled && !hasTankCapacity ? (
            // パターン2: タンク設置済み + 容量未設定 → 警告 + 残量入力
            <>
              <Alert 
                severity="warning" 
                icon={<SettingsIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  <strong>タンク容量が未設定です</strong><br />
                  残量予測を利用するには製品設定でタンク容量を設定してください。
                </Typography>
              </Alert>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!isTankFull} // 容量未設定時は手動入力をデフォルトに
                    onChange={(e) => onTankFullChange(!e.target.checked)}
                    color="primary"
                  />
                }
                label="供給後の残量を記録する"
                sx={{ mb: 2 }}
              />
              
              <Collapse in={!isTankFull}>
                <TextField
                  label="供給後の残量 (L)"
                  fullWidth
                  value={manualRemainingAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d+$/.test(value)) {
                      onManualRemainingAmountChange(value);
                    }
                  }}
                  error={Boolean(manualRemainingError)}
                  helperText={manualRemainingError || "容量未設定のため手動で残量を記録します"}
                  InputProps={{
                    startAdornment: (
                      <WaterDropIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    ),
                    inputProps: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: "0"
                    }
                  }}
                  sx={{ ml: 4 }}
                />
              </Collapse>
            </>
          ) : (
            // パターン3: タンク未設置 → 残量更新チェック
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!isTankFull} // 残量更新するかどうかのチェック
                    onChange={(e) => onTankFullChange(!e.target.checked)}
                    color="primary"
                  />
                }
                label="残量を更新する"
                sx={{ mb: 2 }}
              />
              
              <Collapse in={!isTankFull}>
                <TextField
                  label="供給後の残量 (L)"
                  fullWidth
                  value={manualRemainingAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d+$/.test(value)) {
                      onManualRemainingAmountChange(value);
                    }
                  }}
                  error={Boolean(manualRemainingError)}
                  helperText={manualRemainingError || "供給後の残量を入力してください"}
                  InputProps={{
                    startAdornment: (
                      <WaterDropIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    ),
                    inputProps: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: "0"
                    }
                  }}
                  sx={{ ml: 4 }}
                />
              </Collapse>
            </>
          )}
        </Box>
      )}

      {/* 🚀 予測機能無効時の情報表示 */}
      {isProductSelected && !predictionEnabled && (
        <Alert 
          severity="info" 
          icon={<InfoOutlinedIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            この製品は残量予測機能が無効のため、残量情報は記録されません。
          </Typography>
        </Alert>
      )}

      {/* 特例供給チェック */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        mb: 2
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isExceptional}
              onChange={(e) => onExceptionalChange(e.target.checked)}
              color="primary"
            />
          }
          label="通常と異なる供給（新規・特例）"
        />
        <Tooltip title="通常のスケジュール外で行われる臨時の供給を意味します">
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};

export default SupplyDetails;